import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/synapse/Icon';
import type { IconName } from '@/components/synapse/Icon';
import { TypePill } from '@/components/synapse/TypePill';
import type { ItemType } from '@/types/api';
import type { IssueLines, IssueRow } from './useIssueLines';

const TYPE_ICON: Record<ItemType, IconName> = {
  medication: 'pill',
  consumable: 'box',
  medical_device: 'device',
  reagent: 'flask',
  other: 'box_in',
};

interface IssueLineListProps {
  lines: IssueLines;
}

/** The lines added so far, each with its quantity stepper and a remove button. */
export function IssueLineList({ lines }: IssueLineListProps) {
  return (
    <ul className="flex flex-col gap-2">
      {lines.pending.map((row) => (
        <li key={row.rowId}>
          <IssueListRow
            row={row}
            onRemove={lines.removeRow}
            onChangeQuantity={(q) => lines.updateRowQuantity(row.rowId, q)}
          />
        </li>
      ))}
    </ul>
  );
}

interface IssueListRowProps {
  row: IssueRow;
  onRemove: (rowId: string) => void;
  onChangeQuantity: (q: number) => void;
}

function IssueListRow({ row, onRemove, onChangeQuantity }: IssueListRowProps) {
  const { t } = useTranslation();
  return (
    <div className="flex items-start gap-3 rounded-md border border-line bg-paper p-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-paper-2 text-muted">
        <Icon name={TYPE_ICON[row.type]} size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[14px] font-semibold tracking-[-0.01em]">{row.brandName}</span>
          <span className="text-[13px] text-muted">{row.genericName}</span>
          {row.strength && (
            <span className="font-mono text-[13px] text-ink-2 tabular-nums">
              · {row.strength}
            </span>
          )}
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <TypePill type={row.type} size="sm" />
          <span className="font-mono text-[11.5px] text-muted">
            {row.batchNumber ?? t('issue.batchDash')}
          </span>
          {row.expiryDate && (
            <span className="font-mono text-[11.5px] text-muted">{t('adjust.expDate', { date: row.expiryDate })}</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          aria-label={t('common.decreaseQuantity')}
          onClick={() => onChangeQuantity(row.quantity - 1)}
          className="flex h-8 w-8 items-center justify-center rounded-md border border-line bg-paper text-ink hover:bg-paper-2"
        >
          <Icon name="minus" size={14} />
        </button>
        <span className="min-w-[2ch] text-center font-mono text-[15px] font-semibold tabular-nums">
          {row.quantity}
        </span>
        <button
          type="button"
          aria-label={t('common.increaseQuantity')}
          onClick={() => onChangeQuantity(row.quantity + 1)}
          className="flex h-8 w-8 items-center justify-center rounded-md border border-line bg-paper text-ink hover:bg-paper-2"
        >
          <Icon name="plus" size={14} />
        </button>
      </div>
      <button
        type="button"
        aria-label={`${t('common.remove')} ${row.brandName}`}
        onClick={() => onRemove(row.rowId)}
        className="ml-1 flex h-7 w-7 items-center justify-center rounded-md border border-line bg-paper text-muted hover:text-destructive"
      >
        <Icon name="trash" size={13} />
      </button>
    </div>
  );
}
